/**
 * The frame of the sign-in pages (/Login, /forgot-password, /reset-password):
 * « Retour au site », then the dark card with the logo, the title and the
 * subtitle. One frame so the three pages look the same (B3-Login,
 * B3-forgot-password, B3-reset-password).
 */
import { useState, type ReactNode, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Lock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { BRAND } from '@/config/brand';
import { passwordStrength, STRENGTH_SCORE, type PasswordStrength } from '@/lib/passwordRules';

const LOGO = 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/9d14d2091_288c0f656_2.png';

export const AUTH_INPUT_CLASS = 'pl-11 h-12 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-purple-500 aria-[invalid=true]:border-red-400';
export const AUTH_BUTTON_CLASS = 'w-full h-12 bg-white text-black hover:bg-white/90 rounded-full font-medium';

export function AuthCard({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  const { t } = useTranslation(['admin']);
  return (
    <main className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4 py-10 sm:px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-6 inline-flex min-h-[44px] items-center gap-2 text-sm text-white/60 hover:text-white">
          <ArrowLeft className="w-4 h-4" aria-hidden />
          {t('admin:loginPage.backToSite')}
        </Link>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-8">
            <img src={LOGO} alt="" className="h-8 w-auto" />
            <span className="text-lg font-semibold text-white">{BRAND.name}</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{title}</h1>
          {subtitle ? <p className="text-white/60 mb-8">{subtitle}</p> : <div className="mb-6" />}
          {children}
        </div>
      </div>
    </main>
  );
}

/** A field error under its input, referenced by aria-describedby. */
export function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return <p id={id} className="text-sm text-red-400">{message}</p>;
}

interface PasswordInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  invalid?: boolean;
  describedBy?: string;
  autoFocus?: boolean;
  inputRef?: Ref<HTMLInputElement>;
}

/** Password field with « Afficher / Masquer » (B3-Login). */
export function PasswordInput({ id, value, onChange, autoComplete, invalid, describedBy, autoFocus, inputRef }: PasswordInputProps) {
  const { t } = useTranslation(['admin']);
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" aria-hidden />
      <Input
        id={id}
        ref={inputRef}
        type={shown ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${AUTH_INPUT_CLASS} pr-12`}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
      />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        aria-label={shown ? t('admin:loginPage.hidePassword') : t('admin:loginPage.showPassword')}
        aria-pressed={shown}
        aria-controls={id}
        className="absolute right-0 top-0 flex h-12 w-12 items-center justify-center rounded-r-md text-white/60 hover:text-white"
      >
        {shown ? <EyeOff className="w-5 h-5" aria-hidden /> : <Eye className="w-5 h-5" aria-hidden />}
      </button>
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** i18n key of the email field's error at submit, or undefined. */
export function emailFieldError(email: string): string | undefined {
  if (!email.trim()) return 'admin:loginPage.errors.emailRequired';
  if (!EMAIL_RE.test(email.trim())) return 'admin:loginPage.errors.emailInvalid';
  return undefined;
}

const STRENGTH_COLOR: Record<PasswordStrength, string> = {
  tooShort: 'bg-red-400', weak: 'bg-red-400', fair: 'bg-amber-400', strong: 'bg-emerald-400', veryStrong: 'bg-emerald-400',
};

/** Strength meter under a new password (B3-reset-password): 4 bars and the level in words. */
export function PasswordStrengthMeter({ id, password }: { id: string; password: string }) {
  const { t } = useTranslation(['admin']);
  const level = passwordStrength(password);
  const score = level ? STRENGTH_SCORE[level] : 0;
  return (
    <div id={id} className="space-y-1.5">
      <div className="flex gap-1" aria-hidden>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-1 flex-1 rounded-full ${level && i <= score ? STRENGTH_COLOR[level] : 'bg-white/10'}`} />
        ))}
      </div>
      <p className="text-xs text-white/60" aria-live="polite">
        {level ? t('admin:passwordReset.strength.label', { level: t(`admin:passwordReset.strength.${level}`) }) : t('admin:passwordReset.hint')}
      </p>
    </div>
  );
}
